import React, { useEffect, useState } from 'react';
import { X, Loader2, FileDown, ClipboardList, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { suppliersAPI } from '../services/api';

// Admin: build & generate a supplier purchase order (bon de commande) from low-stock suggestions
const PurchaseOrderModal = ({ supplier, onClose, onCreated }) => {
  const defaultExpected = () => { const d = new Date(); d.setDate(d.getDate() + (parseInt(supplier.lead_time_days, 10) || 0)); return d.toISOString().slice(0, 10); };
  const [threshold, setThreshold] = useState(null);
  const [target, setTarget] = useState(20);
  const [expectedDate, setExpectedDate] = useState(defaultExpected);
  const [lines, setLines] = useState(null);
  const [notes, setNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(!!supplier.email);
  const [showAll, setShowAll] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLines(null);
    suppliersAPI.reorderSuggestions(supplier.id, threshold, target)
      .then(d => { if (!cancelled) { setLines(d.lines); if (threshold == null) setThreshold(d.threshold); } })
      .catch(() => { if (!cancelled) setLines([]); });
    return () => { cancelled = true; };
  }, [supplier.id, threshold, target]);

  const update = (pid, key, value) => setLines(ls => ls.map(l => (l.product_id === pid ? { ...l, [key]: value } : l)));
  const visible = (lines || []).filter(l => showAll || l.low);
  const ordered = (lines || []).filter(l => (parseInt(l.quantity, 10) || 0) > 0);
  const total = ordered.reduce((n, l) => n + (parseFloat(l.purchase_price) || 0) * (parseInt(l.quantity, 10) || 0), 0);

  const generate = async () => {
    if (!ordered.length) { toast.error('Indiquez au moins une quantité à commander'); return; }
    setSaving(true);
    try {
      const po = await suppliersAPI.createPurchaseOrder(supplier.id, {
        lines: ordered.map(l => ({ product_id: l.product_id, name: l.name, brand: l.brand, ref: l.ref, supplier_ref: l.supplier_ref || '', quantity: parseInt(l.quantity, 10), purchase_price: l.purchase_price === '' || l.purchase_price == null ? null : parseFloat(l.purchase_price) })),
        notes,
        send_email: sendEmail,
        expected_date: expectedDate || null,
      });
      await suppliersAPI.downloadPurchaseOrder(po);
      toast.success(`Bon de commande ${po.number} généré${po.email_id ? ` et envoyé à ${supplier.email}` : ''}`);
      onCreated(po);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Génération impossible');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" data-testid="purchase-order-modal">
        <div className="p-5 border-b bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] text-white flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2"><ClipboardList size={20} /> Bon de commande — {supplier.name}</h2>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="purchase-order-close"><X size={22} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <div className="flex flex-wrap items-end gap-4 bg-gray-50 border rounded-lg p-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Seuil de stock bas (≤)</label>
              <Input data-testid="po-threshold-input" type="number" min="0" value={threshold ?? ''} onChange={(e) => setThreshold(Math.max(0, parseInt(e.target.value, 10) || 0))} className="w-28" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Stock cible après livraison</label>
              <Input data-testid="po-target-input" type="number" min="1" value={target} onChange={(e) => setTarget(Math.max(1, parseInt(e.target.value, 10) || 1))} className="w-28" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
              <input type="checkbox" data-testid="po-show-all" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="rounded border-gray-300 text-orange-500" /> Afficher tous les produits de ce fournisseur
            </label>
            <p className="text-xs text-gray-500 pb-2 ml-auto">Quantité proposée = stock cible − stock entrepôt. Modifiable ligne par ligne.</p>
          </div>

          {lines === null ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-500" /></div>
          ) : visible.length === 0 ? (
            <p className="text-sm text-gray-600 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="po-no-low-stock">
              Aucun produit de ce fournisseur n'est à {threshold} unité{threshold > 1 ? 's' : ''} ou moins en entrepôt. Cochez « Afficher tous les produits » pour commander quand même.
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]" data-testid="po-lines-table">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left">Produit</th>
                    <th className="px-3 py-2 text-left">Réf. fourn.</th>
                    <th className="px-3 py-2 text-right">Entrepôt</th>
                    <th className="px-3 py-2 text-right">Chez fourn.</th>
                    <th className="px-3 py-2 text-right w-28">Qté à commander</th>
                    <th className="px-3 py-2 text-right w-28">PU achat HT</th>
                    <th className="px-3 py-2 text-right">Total HT</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visible.map(l => (
                    <tr key={l.product_id} data-testid={`po-line-${l.product_id}`} className={l.low ? '' : 'text-gray-500'}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <img src={l.image} alt="" className="w-9 h-9 object-contain bg-gray-50 rounded" />
                          <div><p className="font-medium text-gray-900">{l.name}</p><p className="text-xs text-gray-400">{l.brand} · Réf. {l.ref}</p></div>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{l.supplier_ref || '—'}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex items-center gap-1 font-semibold ${l.warehouse_stock === 0 ? 'text-red-600' : l.low ? 'text-amber-600' : 'text-gray-700'}`}>{l.low && <AlertTriangle size={12} />}{l.warehouse_stock}</span>
                      </td>
                      <td className="px-3 py-2 text-right">{l.supplier_stock}</td>
                      <td className="px-3 py-2 text-right"><Input data-testid={`po-qty-${l.product_id}`} type="number" min="0" value={l.quantity} onChange={(e) => update(l.product_id, 'quantity', e.target.value)} className="h-8 text-right" /></td>
                      <td className="px-3 py-2 text-right"><Input data-testid={`po-price-${l.product_id}`} type="number" min="0" step="0.01" value={l.purchase_price ?? ''} onChange={(e) => update(l.product_id, 'purchase_price', e.target.value)} placeholder="—" className="h-8 text-right" /></td>
                      <td className="px-3 py-2 text-right font-medium">{((parseFloat(l.purchase_price) || 0) * (parseInt(l.quantity, 10) || 0)).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Remarques pour le fournisseur</label>
            <textarea data-testid="po-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Livraison urgente, horaires de réception…" />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Livraison prévue le</label>
              <Input data-testid="po-expected-date" type="date" min={new Date().toISOString().slice(0, 10)} value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className="w-44" />
            </div>
            <p className="text-xs text-gray-500 pb-2">Par défaut : aujourd'hui + délai fournisseur ({supplier.lead_time_days ?? 0} j). Passé cette date, le bon sera signalé en retard sur le tableau de bord.</p>
          </div>
          <label className={`flex items-center gap-2 text-sm ${supplier.email ? 'cursor-pointer' : 'text-gray-400'}`}>
            <input type="checkbox" data-testid="po-send-email" disabled={!supplier.email} checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="rounded border-gray-300 text-orange-500" />
            Envoyer le bon par e-mail à {supplier.email || 'ce fournisseur (aucune adresse renseignée)'} <span className="text-xs text-gray-400">(simulation — visible dans l'onglet E-mails)</span>
          </label>
        </div>

        <div className="p-4 border-t bg-gray-50 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-gray-700" data-testid="po-summary"><strong>{ordered.length}</strong> article{ordered.length > 1 ? 's' : ''} · <strong>{ordered.reduce((n, l) => n + (parseInt(l.quantity, 10) || 0), 0)}</strong> unités · Total HT <strong className="text-orange-600">{total.toFixed(2)} €</strong></p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} data-testid="po-cancel">Annuler</Button>
            <Button data-testid="po-generate" onClick={generate} disabled={saving || !ordered.length} className="bg-[#1e3a5f] hover:bg-[#2d4a6f]">
              {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileDown size={16} className="mr-2" />} Générer le PDF
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderModal;
